import { FC, ReactNode } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DropResult,
} from "@hello-pangea/dnd";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";

export interface ReorderListItem {
  id: string;
  label: string;
  sublabel?: string;
}

interface ItemReorderListProps {
  items: ReorderListItem[];
  droppableId: string;
  onReorder: (orderedIds: string[]) => void;
  disabled?: boolean;
  renderItemLabel?: (item: ReorderListItem) => ReactNode;
}

const ItemReorderList: FC<ItemReorderListProps> = ({
  items,
  droppableId,
  onReorder,
  disabled = false,
  renderItemLabel,
}) => {
  const moveItem = (itemId: string, direction: "up" | "down") => {
    const index = items.findIndex((item) => item.id === itemId);
    if (index === -1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const reordered = [...items];
    [reordered[index], reordered[newIndex]] = [
      reordered[newIndex],
      reordered[index],
    ];
    onReorder(reordered.map((item) => item.id));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || disabled) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    const reordered = [...items];
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destinationIndex, 0, removed);
    onReorder(reordered.map((item) => item.id));
  };

  const renderRow = (
    item: ReorderListItem,
    index: number,
    draggableProvided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) => (
    <div
      ref={draggableProvided.innerRef}
      {...draggableProvided.draggableProps}
      style={draggableProvided.draggableProps.style}
      className={`flex items-center gap-2 rounded-md border bg-background p-2 ${
        snapshot.isDragging ? "bg-muted/40 shadow-md" : ""
      }`}
    >
      <div
        className={`flex shrink-0 items-center ${
          disabled
            ? "cursor-not-allowed opacity-40"
            : "cursor-grab active:cursor-grabbing"
        }`}
        {...(disabled ? {} : draggableProvided.dragHandleProps)}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        {renderItemLabel ? (
          renderItemLabel(item)
        ) : (
          <div className="truncate text-sm">{item.label}</div>
        )}
        {item.sublabel && (
          <div className="truncate text-xs text-muted-foreground">
            {item.sublabel}
          </div>
        )}
      </div>
      {!disabled && (
        <div className="flex shrink-0 gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={index === 0}
            onClick={() => moveItem(item.id, "up")}
            aria-label="Flyt op"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            disabled={index === items.length - 1}
            onClick={() => moveItem(item.id, "down")}
            aria-label="Flyt ned"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        Ingen elementer at sortere.
      </p>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable
        droppableId={droppableId}
        renderClone={(provided, snapshot, rubric: DraggableRubric) => {
          const item = items[rubric.source.index];
          if (!item) return null;
          return renderRow(item, rubric.source.index, provided, snapshot);
        }}
      >
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="space-y-1"
          >
            {items.map((item, index) => (
              <Draggable
                key={item.id}
                draggableId={item.id}
                index={index}
                isDragDisabled={disabled}
              >
                {(draggableProvided, snapshot) =>
                  renderRow(item, index, draggableProvided, snapshot)
                }
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default ItemReorderList;
